import type { EpochRecord, SpectralState } from "../data/types";
import { hbetaOffsetKms } from "../data/types";
import { LINE_KEYS, isAudible, type LineKey, type LineSolo } from "../domain/lines";
import type { AudioParameters, LayerGains, LineVoiceParams } from "./engine";

/**
 * Bright-state line-flux denominators used to normalise sonified line
 * strengths. These J1628 values are the fallback when a package predates the
 * manifest's ``audio_normalization`` block; newer packages carry their own
 * object's brightest-epoch fluxes and pass them in explicitly.
 */
export const DEFAULT_BRIGHT_FLUX = {
  Halpha: 2770,
  Hbeta: 622,
  OIII: 126,
};

export type BrightFlux = { Halpha: number; Hbeta: number; OIII: number };

const STATE_BLUE_BALANCE = {
  Bright: 1.5,
  Intermediate: 0.9,
  Dim: 0.35,
} as const;

const STATE_RED_BALANCE = {
  Bright: 0.5,
  Intermediate: 0.9,
  Dim: 1.4,
} as const;

// Objects without discrete states sit at the Intermediate balance.
const NEUTRAL_BALANCE = 0.9;

/** Hγ is not tabulated; it is voiced as a fixed fraction of Hβ with Hβ's shape. */
const HGAMMA_TO_HBETA = 0.45;

function stateBalance(
  table: Record<SpectralState, number>,
  state: SpectralState | null,
): number {
  return state ? table[state] : NEUTRAL_BALANCE;
}

/** Per-line voice parameters for one epoch, before blending or soloing. */
function lineParams(epoch: EpochRecord, key: LineKey, bright: BrightFlux): LineVoiceParams {
  const m = epoch.measurements;
  switch (key) {
    case "Halpha":
      return {
        strength: m.Halpha.flux_1e17 / bright.Halpha,
        fwhmKms: m.Halpha.fwhm_kms,
        offsetKms: epoch.kinematics?.Halpha?.peak_offset_kms ?? 0,
      };
    case "Hbeta":
      return {
        strength: m.Hbeta.flux_1e17 / bright.Hbeta,
        fwhmKms: m.Hbeta.fwhm_kms,
        offsetKms: hbetaOffsetKms(epoch) ?? 0,
      };
    case "Hgamma": {
      const hbeta = lineParams(epoch, "Hbeta", bright);
      return { ...hbeta, strength: HGAMMA_TO_HBETA * hbeta.strength };
    }
    case "OIII":
      // The [O III] anchor is a fixed narrow tone; the engine reads only its strength.
      return { strength: m.OIII_5007_flux_1e17.value / bright.OIII, fwhmKms: 0, offsetKms: 0 };
  }
}

/** Scalar audio-relevant quantities of one epoch, before blending. */
function epochScalars(epoch: EpochRecord) {
  return {
    continuumBrightness: Math.pow(10, epoch.measurements.log_l5100.value - 44.0),
    blueBalance: stateBalance(STATE_BLUE_BALANCE, epoch.state),
    redBalance: stateBalance(STATE_RED_BALANCE, epoch.state),
  };
}

function lerp(a: number, b: number, t: number): number {
  return a * (1 - t) + b * t;
}

function lerpLine(a: LineVoiceParams, b: LineVoiceParams, t: number): LineVoiceParams {
  return {
    strength: lerp(a.strength, b.strength, t),
    fwhmKms: lerp(a.fwhmKms, b.fwhmKms, t),
    offsetKms: lerp(a.offsetKms, b.offsetKms, t),
  };
}

/**
 * Translate the current epoch and instrument controls into the audio
 * parameter bundle consumed by ``AudioEngine.update``. With a comparison
 * epoch, every quantity is linearly blended by ``blend`` (0 = primary).
 */
export function epochToAudio(
  primary: EpochRecord,
  comparison: EpochRecord | null,
  blend: number,
  reddeningAv: number,
  layerGains: LayerGains,
  solo: LineSolo,
  brightFlux: BrightFlux = DEFAULT_BRIGHT_FLUX,
): AudioParameters {
  const a = epochScalars(primary);
  const b = comparison ? epochScalars(comparison) : a;
  const t = comparison ? blend : 0;

  const lines = {} as Record<LineKey, LineVoiceParams>;
  for (const key of LINE_KEYS) {
    const la = lineParams(primary, key, brightFlux);
    const blended = comparison ? lerpLine(la, lineParams(comparison, key, brightFlux), t) : la;
    lines[key] = isAudible(key, solo) ? blended : { ...blended, strength: 0 };
  }

  const continuumBrightness = lerp(a.continuumBrightness, b.continuumBrightness, t);
  return {
    continuumBrightness: isAudible("continuum", solo) ? continuumBrightness : 0,
    blueBalance: lerp(a.blueBalance, b.blueBalance, t),
    redBalance: lerp(a.redBalance, b.redBalance, t),
    lines,
    reddeningAv,
    layerGains,
  };
}
